import React from 'react'
import { colors, space } from '@entur/tokens'
import { Heading6, Paragraph, SmallText, StrongText } from '@entur/typography'
import './savings-detail-table.scss'

import {
  getNumberOfSavingDaysInMonth,
  getNumberOfRefundedDaysInMonth,
  Period,
  savingPrintFormat,
} from '../savings-calculations'
import { toPrintFormat } from '../../../utils/date-utils'
import { SavingDetails } from '../../../api-clients/api-types'

type SavingsDetailRowProps = {
  title: string
  date: string
  amount?: string
  amountDescription?: string
  emphasized?: boolean
}
export const SavingsDetailRow: React.FC<SavingsDetailRowProps> = ({
  title,
  date,
  amount,
  amountDescription,
  emphasized = false,
}) => {
  return (
    <div className="detail-row">
      <div>
        <Paragraph style={{ marginTop: 0, marginBottom: -space.extraSmall2 }}>
          {emphasized ? <StrongText>{title}</StrongText> : title}
        </Paragraph>
        <SmallText style={{ color: colors.blues.blue30, lineHeight: '1em' }}>{date}</SmallText>
      </div>
      <div style={{ textAlign: 'right' }}>
        <Paragraph style={{ marginTop: 0, marginBottom: -space.extraSmall2 }}>
          {emphasized ? <StrongText>{amount}</StrongText> : amount}
        </Paragraph>
        <SmallText style={{ color: colors.blues.blue30, lineHeight: '1em' }}>{amountDescription}</SmallText>
      </div>
    </div>
  )
}

type SavingsDetailTableProps = {
  savingDetails: SavingDetails
  calculatedPeriod: Period
  totalTravelDaysForTicket: number
  isWorkTrip?: boolean
  isNotDistributed: boolean
  travelDate: Date
}

export const SavingsDetailTable: React.FC<SavingsDetailTableProps> = ({
  savingDetails,
  calculatedPeriod,
  totalTravelDaysForTicket,
  isWorkTrip,
  isNotDistributed,
  travelDate,
}) => {
  const numberOfSavingDaysInMonth = getNumberOfSavingDaysInMonth(calculatedPeriod)
  const numberOfRefundedDaysInMonth = getNumberOfRefundedDaysInMonth(calculatedPeriod)
  const daysWithSavingsAfterRefund = numberOfSavingDaysInMonth - numberOfRefundedDaysInMonth

  const startDate = isNotDistributed ? toPrintFormat(travelDate) : toPrintFormat(calculatedPeriod.startDate)
  const refundDate = calculatedPeriod.cancelDate ? toPrintFormat(calculatedPeriod.cancelDate) : ''

  return (
    <>
      <Heading6 as="h4" style={{ marginBottom: space.extraSmall, marginTop: space.large }}>
        Utregning av besparelse
      </Heading6>
      <SavingsDetailRow
        title="Billettpris"
        date={startDate}
        amount={savingPrintFormat(savingDetails.ticketPrice)}
        amountDescription={
          isNotDistributed ? '' : `${numberOfSavingDaysInMonth} av ${totalTravelDaysForTicket} dager`
        }
      />
      {savingDetails.additionalProduct && (
        <SavingsDetailRow
          title="Tilleggsprodukt"
          date={startDate}
          amount={savingPrintFormat(savingDetails.additionalProduct)}
          amountDescription={
            isNotDistributed ? '' : `${numberOfSavingDaysInMonth} av ${totalTravelDaysForTicket} dager`
          }
        />
      )}
      {savingDetails.refund && (
        <SavingsDetailRow
          title="Refusjon"
          date={refundDate}
          amount={savingPrintFormat(savingDetails.refund)}
          amountDescription={
            isNotDistributed ? '' : `${numberOfRefundedDaysInMonth} av ${totalTravelDaysForTicket} dager`
          }
        />
      )}
      <SavingsDetailRow
        title="Sum besparelse"
        date={isWorkTrip ? '' : 'Rapporteres til selskapet'}
        amount={savingPrintFormat(savingDetails.saving)}
        amountDescription={
          isNotDistributed ? '' : `${daysWithSavingsAfterRefund} av ${totalTravelDaysForTicket} dager`
        }
        emphasized
      />
    </>
  )
}
